import React, { useState, useEffect, useCallback } from 'react';
import {
  getCashflowProjection, getCashflowChart, getCashflowScenarios,
  createCashflowScenario, deleteCashflowScenario, updateCashflowScenario,
  getCashflowRecurring, createCashflowRecurring, deleteCashflowRecurring,
  createCashflowEntry, getCashflowCategories, getScenarioModifiers,
  createScenarioModifier, deleteScenarioModifier, updateScenarioModifier,
  duplicateScenario, compareCashflowScenarios
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PageTransition, StaggerContainer, StaggerItem } from '../components/motion/Animations';
import { toast } from 'sonner';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, LineChart
} from 'recharts';
import {
  TrendingUp, TrendingDown, AlertTriangle, Plus, Trash2, ChevronDown, ChevronRight,
  BarChart3, Table2, GitBranch, Loader2, ArrowUpRight, ArrowDownRight,
  Repeat, Copy, ToggleLeft, ToggleRight, Columns3, Sparkles, Clock, DollarSign,
  MinusCircle, PlusCircle, Wallet, HelpCircle, Calendar, TrendingDown as TDIcon, Activity
} from 'lucide-react';
import HelpInfoDialog from '../components/help/HelpInfoDialog';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const monthLabel = (mk) => { const [y, m] = (mk || '').split('-'); return `${MONTH_NAMES[parseInt(m) - 1] || ''} ${(y || '').slice(2)}`; };

const MOD_TYPES = {
  delay_collections: { label: 'Retrasar cobros', icon: Clock, color: 'text-amber-600 bg-amber-50' },
  adjust_sales: { label: 'Ajustar ventas', icon: TrendingUp, color: 'text-blue-600 bg-blue-50' },
  add_expense: { label: 'Añadir gasto', icon: MinusCircle, color: 'text-red-600 bg-red-50' },
  remove_expense: { label: 'Eliminar gasto', icon: Trash2, color: 'text-stone-600 bg-stone-50' },
  add_one_off_income: { label: 'Ingreso puntual', icon: PlusCircle, color: 'text-emerald-600 bg-emerald-50' },
  change_initial_balance: { label: 'Saldo inicial', icon: Wallet, color: 'text-violet-600 bg-violet-50' },
};

const TEMPLATES = [
  { name: 'Cliente top retrasa 30 días', mods: [{ type: 'delay_collections', config: { days: 30 }, active: true, order: 0 }] },
  { name: 'Caída ventas 20%', mods: [{ type: 'adjust_sales', config: { percentage: -20 }, active: true, order: 0 }] },
  { name: 'Contratar a alguien nuevo', mods: [{ type: 'add_expense', config: { description: 'Nuevo empleado', amount: 2500, start_date: new Date().toISOString().split('T')[0], recurring: true }, active: true, order: 0 }] },
  { name: 'Financiación 50.000€', mods: [{ type: 'change_initial_balance', config: { amount: 50000 }, active: true, order: 0 }] },
  { name: 'Escenario pesimista', mods: [{ type: 'delay_collections', config: { days: 30 }, active: true, order: 0 }, { type: 'adjust_sales', config: { percentage: -25 }, active: true, order: 1 }] },
  { name: 'Escenario optimista', mods: [{ type: 'adjust_sales', config: { percentage: 15 }, active: true, order: 0 }, { type: 'add_one_off_income', config: { description: 'Nuevo deal', amount: 30000, date: new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0] }, active: true, order: 1 }] },
];

const CashFlowPage = () => {
  const [tab, setTab] = useState('table');
  const [months, setMonths] = useState(6);
  const [projection, setProjection] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [activeScenarioData, setActiveScenarioData] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modifiers, setModifiers] = useState([]);
  const [editingScenario, setEditingScenario] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [compareIds, setCompareIds] = useState([]);

  // Modals
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  // Forms
  const [entryForm, setEntryForm] = useState({ date: new Date().toISOString().split('T')[0], type: 'income', amount: '', description: '', category_id: '' });
  const [recurringForm, setRecurringForm] = useState({ description: '', amount: '', type: 'expense', frequency: 'monthly', start_date: new Date().toISOString().split('T')[0], end_date: '', category_id: '' });
  const [scenarioForm, setScenarioForm] = useState({ name: '', description: '', color: '#0EA5E9' });
  const [modForm, setModForm] = useState({ type: 'delay_collections', config: {}, active: true, order: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [projRes, chartRes, scRes, recRes, catRes] = await Promise.all([
        getCashflowProjection({ months, scenario_id: activeScenario || undefined }),
        getCashflowChart({ months: 12, scenario_id: activeScenario || undefined }),
        getCashflowScenarios(),
        getCashflowRecurring(),
        getCashflowCategories()
      ]);
      setProjection(projRes.data);
      setChartData(chartRes.data.data || []);
      setScenarios(scRes.data);
      setRecurring(recRes.data);
      setCategories(catRes.data);
      setActiveScenarioData(projRes.data?.scenario || null);
      if (!activeScenario && scRes.data.length > 0) {
        const main = scRes.data.find(s => s.is_main);
        if (main) setActiveScenario(main.id);
      }
    } catch { toast.error('Error cargando datos'); }
    finally { setLoading(false); }
  }, [months, activeScenario]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadModifiers = async (scenarioId) => {
    try {
      const { data } = await getScenarioModifiers(scenarioId);
      setModifiers(data);
    } catch { setModifiers([]); }
  };

  // Handlers
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.amount) return;
    try {
      await createCashflowEntry({ ...entryForm, amount: parseFloat(entryForm.amount), scenario_id: activeScenario, category_id: entryForm.category_id || undefined });
      toast.success('Entrada creada'); setShowEntryModal(false); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    try {
      await createCashflowRecurring({ ...recurringForm, amount: parseFloat(recurringForm.amount), end_date: recurringForm.end_date || undefined, category_id: recurringForm.category_id || undefined });
      toast.success('Recurrente creada'); setShowRecurringModal(false); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleCreateScenario = async (e) => {
    e.preventDefault();
    if (!scenarioForm.name) return;
    try {
      const { data } = await createCashflowScenario(scenarioForm);
      toast.success('Escenario creado'); setShowScenarioModal(false); setShowTemplates(false);
      setActiveScenario(data.id); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      const { data: sc } = await createCashflowScenario({ name: template.name, description: `Plantilla: ${template.name}`, color: '#0EA5E9' });
      for (const mod of template.mods) {
        await createScenarioModifier(sc.id, { ...mod, scenario_id: sc.id });
      }
      toast.success(`Escenario "${template.name}" creado`); setShowTemplates(false);
      setActiveScenario(sc.id); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleAddModifier = async (e) => {
    e.preventDefault();
    if (!editingScenario) return;
    try {
      await createScenarioModifier(editingScenario, { ...modForm, scenario_id: editingScenario });
      toast.success('Modificador añadido'); setShowModifierModal(false);
      loadModifiers(editingScenario); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleToggleModifier = async (mod) => {
    try {
      await updateScenarioModifier(mod.id, { active: !mod.active });
      loadModifiers(editingScenario); fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteModifier = async (id) => {
    try { await deleteScenarioModifier(id); loadModifiers(editingScenario); fetchData(); }
    catch { toast.error('Error'); }
  };

  const handleCompare = async () => {
    if (compareIds.length < 2) { toast.error('Selecciona al menos 2 escenarios'); return; }
    try {
      const { data } = await compareCashflowScenarios({ scenario_ids: compareIds.join(','), months });
      setCompareData(data);
    } catch { toast.error('Error comparando'); }
  };

  const toggleCompareId = (id) => setCompareIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 4 ? [...prev, id] : prev);
  const toggleCat = (key) => setExpandedCats(p => ({ ...p, [key]: !p[key] }));
  const alerts = projection?.alerts || [];
  const isMainActive = activeScenarioData?.is_main;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div></div>;

  return (
    <PageTransition>
      <div className="space-y-5">
        {/* Scenario banner */}
        {activeScenarioData && !isMainActive && (
          <div className="rounded-2xl px-4 py-2.5 flex items-center gap-3 text-sm" style={{ backgroundColor: (activeScenarioData.color || '#0EA5E9') + '15', borderLeft: `4px solid ${activeScenarioData.color || '#0EA5E9'}` }} data-testid="scenario-banner">
            <GitBranch className="w-4 h-4" style={{ color: activeScenarioData.color }} />
            <span className="font-semibold" style={{ color: activeScenarioData.color }}>Escenario: {activeScenarioData.name}</span>
            <span className="text-stone-400">Los datos mostrados son una simulación</span>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-stone-950" data-testid="cashflow-title">Cash Flow</h1>
            <p className="text-stone-400 text-sm mt-1">Previsión de tesorería y liquidez</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowHelp(true)} className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="cashflow-help-btn">
              <HelpCircle className="w-4 h-4 mr-1.5" />¿Qué es esto?
            </Button>
            <Select value={activeScenario || ''} onValueChange={(v) => { setActiveScenario(v); setLoading(true); }}>
              <SelectTrigger className="w-52 rounded-full bg-white" data-testid="scenario-select"><SelectValue placeholder="Escenario" /></SelectTrigger>
              <SelectContent>{scenarios.map(s => <SelectItem key={s.id} value={s.id}><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#FF6B4A' }} />{s.name}</span></SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" className="rounded-full" onClick={() => setShowTemplates(true)} data-testid="templates-btn"><Sparkles className="w-4 h-4 mr-1.5" />Plantillas</Button>
            <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50" onClick={() => setShowEntryModal(true)} data-testid="add-entry-btn"><Plus className="w-4 h-4 mr-1.5" />Entrada</Button>
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-start gap-3" data-testid="cashflow-alert">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Alerta de liquidez</p>
              {alerts.map((a, i) => <p key={i} className="text-sm text-red-600">{a.message} (en {a.days_away} días)</p>)}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full w-fit flex-wrap">
          {[
            { key: 'table', icon: Table2, label: 'Tabla' },
            { key: 'chart', icon: BarChart3, label: 'Gráfico' },
            { key: 'scenarios', icon: GitBranch, label: 'Escenarios' },
            { key: 'compare', icon: Columns3, label: 'Comparar' }
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === t.key ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`} data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
          <div className="ml-2 flex items-center gap-1">
            {[3, 6, 12].map(n => (
              <button key={n} onClick={() => { setMonths(n); setLoading(true); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${months === n ? 'bg-[#FF6B4A] text-white' : 'text-stone-400'}`} data-testid={`months-${n}`}>{n}m</button>
            ))}
          </div>
        </div>

        {/* TABLE TAB */}
        {tab === 'table' && projection && (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-auto max-h-[70vh]" data-testid="cashflow-table">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-20 bg-white">
                <tr className="border-b border-stone-100">
                  <th className="text-left py-3 px-5 text-xs uppercase tracking-widest text-stone-400 font-semibold sticky left-0 bg-white z-30 min-w-[180px]">Concepto</th>
                  {projection.months.map(m => (
                    <th key={m.month} className="text-right py-3 px-4 text-xs uppercase tracking-widest text-stone-400 font-semibold min-w-[110px]">{monthLabel(m.month)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-stone-50/50">
                  <td className="py-2.5 px-5 font-semibold text-stone-600 sticky left-0 bg-stone-50/50 z-10 text-xs">SALDO INICIAL</td>
                  {projection.months.map(m => <td key={m.month} className={`py-2.5 px-4 text-right font-bold tabular-nums text-xs ${m.saldo_inicial < 0 ? 'text-red-500' : 'text-stone-600'}`}>{fmt(m.saldo_inicial)}</td>)}
                </tr>
                <tr className="border-t border-stone-100"><td className="py-2.5 px-5 font-bold text-emerald-700 sticky left-0 bg-white z-10 text-xs flex items-center gap-1.5"><ArrowDownRight className="w-3.5 h-3.5" />COBROS</td>{projection.months.map(m => <td key={m.month} className="py-2.5 px-4 text-right font-bold text-emerald-600 tabular-nums text-xs">{fmt(m.income_total)}</td>)}</tr>
                {projection.months[0]?.income_by_category?.map((cat, ci) => (
                  <tr key={`inc-${ci}`} className="hover:bg-orange-50/30 cursor-pointer" onClick={() => toggleCat(`inc-${ci}`)}>
                    <td className="py-1.5 px-5 pl-9 text-stone-500 sticky left-0 bg-white z-10 text-xs flex items-center gap-1.5">
                      {expandedCats[`inc-${ci}`] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />{cat.name}
                    </td>
                    {projection.months.map(m => { const c = m.income_by_category?.find(x => x.name === cat.name); return <td key={m.month} className="py-1.5 px-4 text-right text-emerald-600/60 tabular-nums text-xs">{c ? fmt(c.amount) : '-'}</td>; })}
                  </tr>
                ))}
                <tr className="border-t border-stone-100"><td className="py-2.5 px-5 font-bold text-red-700 sticky left-0 bg-white z-10 text-xs flex items-center gap-1.5"><ArrowUpRight className="w-3.5 h-3.5" />PAGOS</td>{projection.months.map(m => <td key={m.month} className="py-2.5 px-4 text-right font-bold text-red-500 tabular-nums text-xs">{fmt(m.expense_total)}</td>)}</tr>
                {projection.months[0]?.expense_by_category?.map((cat, ci) => (
                  <tr key={`exp-${ci}`} className="hover:bg-orange-50/30 cursor-pointer" onClick={() => toggleCat(`exp-${ci}`)}>
                    <td className="py-1.5 px-5 pl-9 text-stone-500 sticky left-0 bg-white z-10 text-xs flex items-center gap-1.5">
                      <ChevronRight className="w-3 h-3" /><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />{cat.name}
                    </td>
                    {projection.months.map(m => { const c = m.expense_by_category?.find(x => x.name === cat.name); return <td key={m.month} className="py-1.5 px-4 text-right text-red-500/60 tabular-nums text-xs">{c ? fmt(c.amount) : '-'}</td>; })}
                  </tr>
                ))}
                <tr className="border-t-2 border-stone-200 bg-stone-50/50">
                  <td className="py-3 px-5 font-bold text-stone-900 sticky left-0 bg-stone-50/50 z-10 text-sm">SALDO FINAL</td>
                  {projection.months.map(m => (
                    <td key={m.month} className={`py-3 px-4 text-right font-bold tabular-nums ${m.saldo_final < 0 ? 'text-red-500' : 'text-stone-900'}`}>
                      <div className="text-sm">{fmt(m.saldo_final)}</div>
                      {m.variation_pct !== 0 && <span className={`text-[10px] ${m.variation_pct > 0 ? 'text-emerald-500' : 'text-red-400'}`}>{m.variation_pct > 0 ? '+' : ''}{m.variation_pct}%</span>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* CHART TAB */}
        {tab === 'chart' && (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6" data-testid="cashflow-chart-container">
            <h3 className="text-lg font-heading font-bold text-stone-900 mb-1">Evolución de Tesorería</h3>
            <p className="text-xs text-stone-400 mb-6">Saldo de caja vs beneficio contable</p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: '#78716C' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#78716C' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtFull(v)} labelFormatter={monthLabel} contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                  <Legend />
                  <ReferenceLine y={0} stroke="#F43F5E" strokeDasharray="3 3" strokeWidth={1.5} />
                  <Bar dataKey="ingresos" fill="#10B981" name="Ingresos" radius={[4, 4, 0, 0]} opacity={0.25} />
                  <Bar dataKey="gastos" fill="#F43F5E" name="Gastos" radius={[4, 4, 0, 0]} opacity={0.25} />
                  <Line type="monotone" dataKey="saldo" stroke="#FF6B4A" strokeWidth={3} name="Saldo" dot={(props) => { const { cx, cy, value } = props; return <circle cx={cx} cy={cy} r={5} fill={value < 0 ? '#F43F5E' : '#FF6B4A'} stroke="white" strokeWidth={2} />; }} />
                  <Line type="monotone" dataKey="beneficio" stroke="#0EA5E9" strokeWidth={2} strokeDasharray="6 3" name="Beneficio" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* SCENARIOS TAB */}
        {tab === 'scenarios' && (
          <div className="space-y-4" data-testid="scenarios-tab">
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => { setScenarioForm({ name: '', description: '', color: '#0EA5E9' }); setShowScenarioModal(true); }}><Plus className="w-4 h-4 mr-1.5" />Nuevo escenario</Button>
              <Button variant="outline" className="rounded-full" onClick={() => setShowTemplates(true)}><Sparkles className="w-4 h-4 mr-1.5" />Plantillas</Button>
              <Button variant="outline" className="rounded-full" onClick={() => setShowRecurringModal(true)}><Repeat className="w-4 h-4 mr-1.5" />Recurrente</Button>
            </div>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {scenarios.map(s => (
                <StaggerItem key={s.id}>
                  <div className={`bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border p-5 transition-all ${activeScenario === s.id ? 'ring-2' : 'border-stone-100'}`} style={activeScenario === s.id ? { borderColor: s.color, ringColor: s.color + '20' } : {}} data-testid={`scenario-card-${s.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || '#FF6B4A' }} />
                        <h3 className="font-bold text-stone-900 text-sm">{s.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        {!s.is_main && (
                          <>
                            <button onClick={async () => { setEditingScenario(s.id); await loadModifiers(s.id); setTab('scenarios'); }} className="p-1 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" title="Editar modificadores"><GitBranch className="w-3.5 h-3.5" /></button>
                            <button onClick={async () => { await duplicateScenario(s.id, { name: `${s.name} (copia)` }); fetchData(); toast.success('Duplicado'); }} className="p-1 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" title="Duplicar"><Copy className="w-3.5 h-3.5" /></button>
                            <button onClick={async () => { if (window.confirm('¿Eliminar?')) { await deleteCashflowScenario(s.id); if (activeScenario === s.id) setActiveScenario(null); fetchData(); } }} className="p-1 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </div>
                    </div>
                    {s.is_main && <span className="text-[9px] px-2 py-0.5 bg-orange-50 text-[#FF6B4A] rounded-full font-bold uppercase tracking-wider">Principal</span>}
                    {s.description && <p className="text-xs text-stone-400 mt-1.5 line-clamp-2">{s.description}</p>}
                    {s.modifier_count > 0 && <p className="text-xs text-stone-500 mt-1">{s.modifier_count} modificadores</p>}
                    <Button variant={activeScenario === s.id ? 'default' : 'outline'} size="sm" className={`mt-3 rounded-full w-full text-xs ${activeScenario === s.id ? '' : ''}`} style={activeScenario === s.id ? { backgroundColor: s.color } : {}} onClick={() => { setActiveScenario(s.id); setTab('table'); setLoading(true); }}>
                      {activeScenario === s.id ? 'Activo' : 'Activar'}
                    </Button>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>

            {/* Modifier editor */}
            {editingScenario && !scenarios.find(s => s.id === editingScenario)?.is_main && (
              <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-heading font-bold text-stone-900">Modificadores: {scenarios.find(s => s.id === editingScenario)?.name}</h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setModForm({ type: 'delay_collections', config: {}, active: true, order: modifiers.length }); setShowModifierModal(true); }}><Plus className="w-3.5 h-3.5 mr-1" />Añadir</Button>
                    <button onClick={() => setEditingScenario(null)} className="p-1.5 hover:bg-stone-100 rounded-lg text-stone-400">✕</button>
                  </div>
                </div>
                {modifiers.length === 0 ? (
                  <p className="text-sm text-stone-400">Sin modificadores. Añade uno para simular cambios.</p>
                ) : (
                  <div className="space-y-2">
                    {modifiers.map(mod => {
                      const mt = MOD_TYPES[mod.type] || { label: mod.type, icon: GitBranch, color: 'text-stone-600 bg-stone-50' };
                      const Icon = mt.icon;
                      return (
                        <div key={mod.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${mod.active ? 'border-stone-200 bg-white' : 'border-stone-100 bg-stone-50 opacity-60'}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${mt.color}`}><Icon className="w-4 h-4" /></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-800">{mt.label}</p>
                            <p className="text-xs text-stone-400 truncate">{JSON.stringify(mod.config).replace(/[{}"]/g, '').replace(/,/g, ', ')}</p>
                          </div>
                          <button onClick={() => handleToggleModifier(mod)} className="p-1">{mod.active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-stone-300" />}</button>
                          <button onClick={() => handleDeleteModifier(mod.id)} className="p-1 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-stone-300" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Recurring */}
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-heading font-bold text-stone-900 text-sm">Entradas Recurrentes</h3>
                <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setShowRecurringModal(true)}><Plus className="w-3.5 h-3.5 mr-1" />Añadir</Button>
              </div>
              {recurring.length === 0 ? <p className="text-xs text-stone-400">Añade nóminas, alquileres o suscripciones.</p> : (
                <div className="divide-y divide-stone-50">{recurring.map(r => (
                  <div key={r.id} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${r.type === 'income' ? 'bg-emerald-50' : 'bg-red-50'}`}><Repeat className={`w-3.5 h-3.5 ${r.type === 'income' ? 'text-emerald-500' : 'text-red-500'}`} /></div>
                      <div><p className="text-xs font-medium text-stone-800">{r.description}</p><p className="text-[10px] text-stone-400">{r.frequency === 'monthly' ? 'Mensual' : r.frequency === 'quarterly' ? 'Trimestral' : 'Anual'}</p></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-xs tabular-nums ${r.type === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>{r.type === 'expense' ? '-' : '+'}{fmtFull(r.amount)}</span>
                      <button onClick={() => handleDeleteRecurring(r.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3 text-stone-300" /></button>
                    </div>
                  </div>
                ))}</div>
              )}
            </div>
          </div>
        )}

        {/* COMPARE TAB */}
        {tab === 'compare' && (
          <div className="space-y-4" data-testid="compare-tab">
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
              <h3 className="font-heading font-bold text-stone-900 mb-3">Selecciona hasta 4 escenarios</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {scenarios.map(s => (
                  <button key={s.id} onClick={() => toggleCompareId(s.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${compareIds.includes(s.id) ? 'text-white' : 'text-stone-600 border-stone-200'}`} style={compareIds.includes(s.id) ? { backgroundColor: s.color, borderColor: s.color } : {}}>
                    {s.name}
                  </button>
                ))}
              </div>
              <Button onClick={handleCompare} disabled={compareIds.length < 2} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="compare-btn">
                <Columns3 className="w-4 h-4 mr-1.5" />Comparar ({compareIds.length})
              </Button>
            </div>

            {compareData && compareData.scenarios?.length > 0 && (
              <>
                {/* Comparison table */}
                <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b border-stone-100">
                        <th className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest text-stone-400 font-semibold sticky left-0 bg-white z-20 min-w-[140px]">Escenario</th>
                        {compareData.scenarios[0]?.months?.map(m => (
                          <th key={m.month} className="text-right py-2.5 px-3 text-[10px] uppercase tracking-widest text-stone-400 font-semibold min-w-[90px]">{monthLabel(m.month)}</th>
                        ))}
                        <th className="text-right py-2.5 px-4 text-[10px] uppercase tracking-widest text-stone-400 font-semibold min-w-[100px]">Diferencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compareData.scenarios.map((sc, si) => {
                        const mainSc = compareData.scenarios.find(s => s.is_main);
                        const lastMonth = sc.months?.[sc.months.length - 1];
                        const mainLast = mainSc?.months?.[mainSc.months.length - 1];
                        const diff = lastMonth && mainLast && !sc.is_main ? lastMonth.saldo_final - mainLast.saldo_final : 0;
                        return (
                          <tr key={sc.scenario_id} className="border-b border-stone-50">
                            <td className="py-2.5 px-4 sticky left-0 bg-white z-10">
                              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: sc.scenario_color }} /><span className="font-semibold text-stone-800">{sc.scenario_name}</span></div>
                            </td>
                            {sc.months?.map(m => {
                              const mainMonth = mainSc?.months?.find(mm => mm.month === m.month);
                              const mdiff = mainMonth && !sc.is_main ? m.saldo_final - mainMonth.saldo_final : 0;
                              const bgIntensity = Math.min(Math.abs(mdiff) / 10000, 1);
                              const bg = mdiff > 0 ? `rgba(16,185,129,${bgIntensity * 0.15})` : mdiff < 0 ? `rgba(244,63,94,${bgIntensity * 0.15})` : 'transparent';
                              return (
                                <td key={m.month} className="py-2.5 px-3 text-right tabular-nums font-medium" style={{ backgroundColor: bg }}>
                                  <span className={m.saldo_final < 0 ? 'text-red-500' : 'text-stone-700'}>{fmt(m.saldo_final)}</span>
                                </td>
                              );
                            })}
                            <td className={`py-2.5 px-4 text-right font-bold tabular-nums ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-stone-400'}`}>
                              {sc.is_main ? '-' : `${diff > 0 ? '+' : ''}${fmt(diff)}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Comparison chart */}
                <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6">
                  <h3 className="text-sm font-heading font-bold text-stone-900 mb-4">Evolución comparada</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                        <XAxis dataKey="month" type="category" allowDuplicatedCategory={false} tickFormatter={monthLabel} tick={{ fontSize: 11, fill: '#78716C' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#78716C' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => fmtFull(v)} labelFormatter={monthLabel} contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                        <Legend />
                        <ReferenceLine y={0} stroke="#F43F5E" strokeDasharray="3 3" />
                        {compareData.scenarios.map(sc => (
                          <Line key={sc.scenario_id} data={sc.months?.map(m => ({ month: m.month, [sc.scenario_name]: m.saldo_final }))} type="monotone" dataKey={sc.scenario_name} stroke={sc.scenario_color} strokeWidth={sc.is_main ? 3 : 2} strokeDasharray={sc.is_main ? '0' : '6 3'} dot={{ r: 3, fill: sc.scenario_color }} name={sc.scenario_name} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Summary */}
                  <div className="mt-4 space-y-1.5">
                    {compareData.scenarios.filter(s => !s.is_main).map(sc => {
                      const mainSc = compareData.scenarios.find(s => s.is_main);
                      const lastM = sc.months?.[sc.months.length - 1];
                      const mainLastM = mainSc?.months?.[mainSc.months.length - 1];
                      const d = lastM && mainLastM ? lastM.saldo_final - mainLastM.saldo_final : 0;
                      return (
                        <p key={sc.scenario_id} className="text-xs">
                          <span className="font-semibold" style={{ color: sc.scenario_color }}>{sc.scenario_name}</span>
                          {' termina con '}
                          <span className={`font-bold ${d >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{d >= 0 ? '+' : ''}{fmtFull(d)}</span>
                          {' vs principal'}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* MODALS */}
        {/* Entry */}
        <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
          <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="font-heading font-extrabold">Nueva Entrada</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateEntry} className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={entryForm.date} onChange={e => setEntryForm({ ...entryForm, date: e.target.value })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div><div className="space-y-1"><Label className="text-xs">Tipo</Label><Select value={entryForm.type} onValueChange={v => setEntryForm({ ...entryForm, type: v })}><SelectTrigger className="rounded-xl bg-stone-50 border-transparent h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="income">Cobro</SelectItem><SelectItem value="expense">Pago</SelectItem></SelectContent></Select></div></div>
              <div className="space-y-1"><Label className="text-xs">Descripción</Label><Input value={entryForm.description} onChange={e => setEntryForm({ ...entryForm, description: e.target.value })} placeholder="Concepto" className="rounded-xl bg-stone-50 border-transparent h-9" /></div>
              <div className="space-y-1"><Label className="text-xs">Importe</Label><Input type="number" step="0.01" value={entryForm.amount} onChange={e => setEntryForm({ ...entryForm, amount: e.target.value })} placeholder="0.00" className="rounded-xl bg-stone-50 border-transparent h-9 font-bold" required /></div>
              <div className="flex gap-3 pt-1"><Button type="button" variant="outline" onClick={() => setShowEntryModal(false)} className="flex-1 rounded-full h-9">Cancelar</Button><Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full h-9">Crear</Button></div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Recurring */}
        <Dialog open={showRecurringModal} onOpenChange={setShowRecurringModal}>
          <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="font-heading font-extrabold">Entrada Recurrente</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateRecurring} className="space-y-3 mt-2">
              <div className="space-y-1"><Label className="text-xs">Descripción *</Label><Input value={recurringForm.description} onChange={e => setRecurringForm({ ...recurringForm, description: e.target.value })} placeholder="Ej: Alquiler" className="rounded-xl bg-stone-50 border-transparent h-9" required /></div>
              <div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="text-xs">Importe *</Label><Input type="number" step="0.01" value={recurringForm.amount} onChange={e => setRecurringForm({ ...recurringForm, amount: e.target.value })} className="rounded-xl bg-stone-50 border-transparent h-9 font-bold" required /></div><div className="space-y-1"><Label className="text-xs">Tipo</Label><Select value={recurringForm.type} onValueChange={v => setRecurringForm({ ...recurringForm, type: v })}><SelectTrigger className="rounded-xl bg-stone-50 border-transparent h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="income">Cobro</SelectItem><SelectItem value="expense">Pago</SelectItem></SelectContent></Select></div></div>
              <div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="text-xs">Frecuencia</Label><Select value={recurringForm.frequency} onValueChange={v => setRecurringForm({ ...recurringForm, frequency: v })}><SelectTrigger className="rounded-xl bg-stone-50 border-transparent h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Mensual</SelectItem><SelectItem value="quarterly">Trimestral</SelectItem><SelectItem value="annual">Anual</SelectItem></SelectContent></Select></div><div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={recurringForm.start_date} onChange={e => setRecurringForm({ ...recurringForm, start_date: e.target.value })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div></div>
              <div className="flex gap-3 pt-1"><Button type="button" variant="outline" onClick={() => setShowRecurringModal(false)} className="flex-1 rounded-full h-9">Cancelar</Button><Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full h-9">Crear</Button></div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Scenario */}
        <Dialog open={showScenarioModal} onOpenChange={setShowScenarioModal}>
          <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="font-heading font-extrabold">Nuevo Escenario</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateScenario} className="space-y-3 mt-2">
              <div className="space-y-1"><Label className="text-xs">Nombre *</Label><Input value={scenarioForm.name} onChange={e => setScenarioForm({ ...scenarioForm, name: e.target.value })} placeholder='Ej: "Si pierdo al cliente X"' className="rounded-xl bg-stone-50 border-transparent h-9" required /></div>
              <div className="space-y-1"><Label className="text-xs">Descripción</Label><Input value={scenarioForm.description} onChange={e => setScenarioForm({ ...scenarioForm, description: e.target.value })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div>
              <div className="space-y-1"><Label className="text-xs">Color</Label><div className="flex gap-2">{['#FF6B4A', '#0EA5E9', '#10B981', '#F43F5E', '#8B5CF6', '#FBBF24'].map(c => (<button key={c} type="button" onClick={() => setScenarioForm({ ...scenarioForm, color: c })} className={`w-7 h-7 rounded-full border-2 transition-all ${scenarioForm.color === c ? 'border-stone-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />))}</div></div>
              <div className="flex gap-3 pt-1"><Button type="button" variant="outline" onClick={() => setShowScenarioModal(false)} className="flex-1 rounded-full h-9">Cancelar</Button><Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full h-9">Crear</Button></div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Templates */}
        <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
          <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle className="font-heading font-extrabold">Plantillas de Escenario</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {TEMPLATES.map((t, i) => (
                <button key={i} onClick={() => handleCreateFromTemplate(t)} className="text-left p-4 rounded-xl border border-stone-200 hover:border-[#FF6B4A] hover:shadow-md transition-all group" data-testid={`template-${i}`}>
                  <p className="text-sm font-semibold text-stone-800 group-hover:text-[#FF6B4A]">{t.name}</p>
                  <p className="text-xs text-stone-400 mt-1">{t.mods.length} modificador{t.mods.length > 1 ? 'es' : ''}</p>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modifier */}
        <Dialog open={showModifierModal} onOpenChange={setShowModifierModal}>
          <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="font-heading font-extrabold">Añadir Modificador</DialogTitle></DialogHeader>
            <form onSubmit={handleAddModifier} className="space-y-3 mt-2">
              <div className="space-y-1"><Label className="text-xs">Tipo</Label>
                <Select value={modForm.type} onValueChange={v => setModForm({ ...modForm, type: v, config: {} })}>
                  <SelectTrigger className="rounded-xl bg-stone-50 border-transparent h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MOD_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {modForm.type === 'delay_collections' && (<div className="space-y-1"><Label className="text-xs">Días de retraso</Label><Input type="number" value={modForm.config.days || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, days: parseInt(e.target.value) || 0 } })} placeholder="30" className="rounded-xl bg-stone-50 border-transparent h-9" /></div>)}
              {modForm.type === 'adjust_sales' && (<div className="space-y-1"><Label className="text-xs">Porcentaje (negativo=reducción)</Label><Input type="number" value={modForm.config.percentage || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, percentage: parseInt(e.target.value) || 0 } })} placeholder="-20" className="rounded-xl bg-stone-50 border-transparent h-9" /></div>)}
              {modForm.type === 'add_expense' && (<><div className="space-y-1"><Label className="text-xs">Descripción</Label><Input value={modForm.config.description || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, description: e.target.value } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div><div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="text-xs">Importe €/mes</Label><Input type="number" value={modForm.config.amount || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, amount: parseFloat(e.target.value) || 0, recurring: true } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div><div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={modForm.config.start_date || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, start_date: e.target.value } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div></div></>)}
              {modForm.type === 'add_one_off_income' && (<><div className="space-y-1"><Label className="text-xs">Descripción</Label><Input value={modForm.config.description || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, description: e.target.value } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div><div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="text-xs">Importe</Label><Input type="number" value={modForm.config.amount || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, amount: parseFloat(e.target.value) || 0 } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div><div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={modForm.config.date || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, date: e.target.value } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div></div></>)}
              {modForm.type === 'change_initial_balance' && (<div className="space-y-1"><Label className="text-xs">Saldo inicial (€)</Label><Input type="number" step="0.01" value={modForm.config.amount || ''} onChange={e => setModForm({ ...modForm, config: { ...modForm.config, amount: parseFloat(e.target.value) || 0 } })} className="rounded-xl bg-stone-50 border-transparent h-9" /></div>)}
              <div className="flex gap-3 pt-1"><Button type="button" variant="outline" onClick={() => setShowModifierModal(false)} className="flex-1 rounded-full h-9">Cancelar</Button><Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full h-9">Añadir</Button></div>
            </form>
          </DialogContent>
        </Dialog>

        <HelpInfoDialog
          open={showHelp}
          onClose={() => setShowHelp(false)}
          icon={Wallet}
          title="¿Qué es Cash Flow?"
          intro={(
            <>
              <p><strong>Tesorería proyectada</strong>. Te muestra cuánto dinero tendrás en el banco
                dentro de 1, 3 o 6 meses combinando lo que <strong>ya tienes</strong> (saldo actual) con lo que
                <strong> esperas cobrar</strong> (facturas pendientes, suscripciones recurrentes) y <strong>lo que tendrás que pagar</strong>
                (gastos, impuestos, nóminas).</p>
              <p className="mt-2">El objetivo: ver con tiempo si vas a entrar en números rojos para reaccionar antes
                (acelerar cobros, retrasar pagos, pedir un anticipo).</p>
            </>
          )}
          sections={[
            {
              icon: BarChart3, iconColor: '#FF6B4A', title: 'Qué verás',
              body: [
                '<strong>Saldo proyectado mes a mes</strong> en gráfico y tabla',
                '<strong>Alertas en rojo</strong> si vas a quedarte por debajo de tu colchón mínimo',
                '<strong>Entradas previstas</strong>: facturas por cobrar + suscripciones',
                '<strong>Salidas previstas</strong>: gastos recurrentes, impuestos a pagar',
              ],
            },
            {
              icon: GitBranch, iconColor: '#7C3AED', title: 'Escenarios',
              body: [
                '<strong>Realista</strong>: tu situación actual sin cambios',
                '<strong>Optimista</strong>: ¿qué pasa si cierro 2 clientes nuevos a 3.000 €?',
                '<strong>Pesimista</strong>: ¿qué pasa si pierdo el cliente X?',
                'Compara los 3 en el mismo gráfico para tomar decisiones',
              ],
            },
            {
              icon: Sparkles, iconColor: '#0EA5E9', title: 'Plantillas rápidas',
              body: 'Botón "Plantillas" crea escenarios predefinidos (contratación de un empleado, compra de furgoneta, etc.) en 1 clic con cifras realistas.',
            },
            {
              icon: TrendingUp, iconColor: '#10B981', title: 'Para qué te sirve realmente',
              body: [
                'Decidir <strong>cuándo contratar</strong> un empleado sin ponerte en riesgo',
                'Saber si puedes <strong>permitirte una inversión</strong> grande este trimestre',
                'Anticipar el <strong>cierre del año fiscal</strong> y planificar IVA/IRPF',
                'Detectar meses problemáticos (verano, navidades) y prepararte',
              ],
            },
          ]}
        />
      </div>
    </PageTransition>
  );
};

export default CashFlowPage;
